import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Download, Pencil, Plus, Trash2, Wallet } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  MasterSummaryStrip,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useClients } from "@/features/clients/api";
import { formatMoney } from "@/features/clients/constants";
import {
  expenseToForm,
  useDeleteExpense,
  useExpenses,
  useUpsertExpense,
} from "@/features/expenses/api";
import {
  EXPENSE_CATEGORIES,
  EXPENSE_PAYMENT_METHODS,
  labelFor,
} from "@/features/expenses/constants";
import {
  emptyExpenseForm,
  expenseFormSchema,
  type ExpenseFormValues,
} from "@/features/expenses/schema";
import type { ExpenseListRow } from "@/features/expenses/types";
import { useVendors } from "@/features/vendors/api";
import { isSupabaseConfigured } from "@/lib/supabase";

export function ExpensesPage() {
  return (
    <ErrorBoundary title="Expenses page crashed">
      <ExpensesPageInner />
    </ErrorBoundary>
  );
}

function ExpensesPageInner() {
  const { data = [], isLoading, error, refetch, isFetching } = useExpenses();
  const { data: clients = [] } = useClients();
  const { data: vendors = [] } = useVendors();
  const upsert = useUpsertExpense();
  const remove = useDeleteExpense();

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseListRow | null>(null);
  const [deleting, setDeleting] = useState<ExpenseListRow | null>(null);

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: emptyExpenseForm,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(editing ? expenseToForm(editing) : emptyExpenseForm);
  }, [open, editing, form]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (categoryFilter !== "all" && row.category !== categoryFilter) return false;
      if (from && row.expense_date < from) return false;
      if (to && row.expense_date > to) return false;
      if (!q) return true;
      return [row.title, row.reference, row.notes, row.client_name, row.vendor_name, row.category]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, categoryFilter, from, to]);

  const total = useMemo(
    () => filtered.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [filtered],
  );

  const errors = form.formState.errors;

  const startCreate = useCallback(() => {
    setEditing(null);
    setOpen(true);
  }, []);

  useMasterListShortcuts({
    searchInputId: "expenses-search",
    onCreate: startCreate,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
    setEditing(null);
  });

  function exportCsv() {
    const header = [
      "date",
      "category",
      "title",
      "amount",
      "payment_method",
      "reference",
      "client",
      "vendor",
      "notes",
    ];
    const body = filtered.map((r) =>
      [
        r.expense_date,
        r.category,
        csv(r.title),
        r.amount,
        r.payment_method,
        csv(r.reference ?? ""),
        csv(r.client_name ?? ""),
        csv(r.vendor_name ?? ""),
        csv(r.notes ?? ""),
      ].join(","),
    );
    const blob = new Blob([[header.join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expenses-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to manage expenses."
      />
    );
  }

  return (
    <MasterPage>
      <MasterHeader
        title="Expenses"
        icon={<Wallet />}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!filtered.length}
            >
              <Download />
              Export
            </Button>
            <Button type="button" size="sm" onClick={startCreate}>
              <Plus />
              Add expense
            </Button>
          </>
        }
      />

      <MasterSummaryStrip
        items={[
          { label: "Total (filtered)", value: formatMoney(total), tone: "warning" },
          { label: "Entries", value: filtered.length },
        ]}
      />

      <MasterFilterBar>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="h-8 w-36"
          aria-label="From date"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="h-8 w-36"
          aria-label="To date"
        />
        <NativeSelect
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-8 w-48"
          aria-label="Filter by category"
        >
          <option value="all">All categories</option>
          {EXPENSE_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </NativeSelect>
        <MasterSearchInput
          id="expenses-search"
          value={search}
          onChange={setSearch}
          placeholder="Search…  (/)"
          className="[&_input]:h-8"
        />
      </MasterFilterBar>

      {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

      {isLoading ? (
        <p className="py-12 text-center text-xs text-muted-foreground">Loading…</p>
      ) : data.length === 0 ? (
        <EmptyState
          title="No expenses yet"
          description="Log fuel, salaries, client lunches and other operating costs — they appear on the master ledger automatically."
          action={
            <Button type="button" size="sm" onClick={startCreate}>
              <Plus />
              Add expense
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Paid via</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6} className="py-10 text-center text-xs text-muted-foreground">
                    No expenses match the filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} className="group">
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {format(parseISO(row.expense_date), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{labelFor(EXPENSE_CATEGORIES, row.category)}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs font-medium">{row.title}</div>
                      <div className="text-2xs text-muted-foreground">
                        {[row.client_name, row.vendor_name, row.reference].filter(Boolean).join(" · ") ||
                          "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {labelFor(EXPENSE_PAYMENT_METHODS, row.payment_method)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums text-warning">
                      {formatMoney(row.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Edit expense"
                          onClick={() => {
                            setEditing(row);
                            setOpen(true);
                          }}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Delete expense"
                          className="hover:text-destructive"
                          onClick={() => setDeleting(row)}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing ? "Edit expense" : "New expense"}</SheetTitle>
            <SheetDescription>
              Saved expenses show under Finance → Master ledger with type Expense.
            </SheetDescription>
          </SheetHeader>

          <form id="expense-form" onSubmit={onSubmit} className="flex-1 overflow-y-auto p-4">
            <FieldGrid>
              <Field label="Date" required span={6} error={errors.expense_date?.message}>
                <Input type="date" {...form.register("expense_date")} />
              </Field>
              <Field label="Category" span={6}>
                <NativeSelect {...form.register("category")}>
                  {EXPENSE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Title" required span={12} error={errors.title?.message}>
                <Input {...form.register("title")} placeholder="Diesel for dispatch van" autoFocus />
              </Field>
              <Field label="Amount" required span={6} error={errors.amount?.message}>
                <Input type="number" step="0.01" min="0" {...form.register("amount")} />
              </Field>
              <Field label="Paid via" span={6}>
                <NativeSelect {...form.register("payment_method")}>
                  {EXPENSE_PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Reference" span={12} hint="Bill no. / UPI ref">
                <Input {...form.register("reference")} />
              </Field>
              <Field label="Linked client" span={6} hint="Optional — e.g. client lunch">
                <EntityPicker
                  allowClear
                  value={form.watch("client_id") ?? ""}
                  recentKey="clients"
                  placeholder="Search client…"
                  options={clients
                    .filter((c) => c.is_active || c.id === form.watch("client_id"))
                    .map((c) => ({
                      id: c.id,
                      label: `${c.code} · ${c.name}`,
                      keywords: `${c.code} ${c.name} ${c.gst_no ?? ""}`,
                      meta: c.city || undefined,
                    }))}
                  onChange={(id) =>
                    form.setValue("client_id", id, { shouldDirty: true })
                  }
                />
              </Field>
              <Field label="Linked vendor" span={6} hint="Optional">
                <EntityPicker
                  allowClear
                  value={form.watch("vendor_id") ?? ""}
                  recentKey="vendors"
                  placeholder="Search vendor…"
                  options={vendors
                    .filter((v) => v.is_active || v.id === form.watch("vendor_id"))
                    .map((v) => ({
                      id: v.id,
                      label: `${v.code} · ${v.name}`,
                      keywords: `${v.code} ${v.name} ${v.gstin ?? ""}`,
                      meta: v.city || undefined,
                    }))}
                  onChange={(id) =>
                    form.setValue("vendor_id", id, { shouldDirty: true })
                  }
                />
              </Field>
              <Field label="Notes" span={12}>
                <Textarea rows={2} {...form.register("notes")} />
              </Field>
            </FieldGrid>
          </form>

          <SheetFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="expense-form" size="sm" disabled={upsert.isPending}>
              {editing ? "Save changes" : "Add expense"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete expense?"
        description={deleting ? `${deleting.title} will be removed from the ledger.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </MasterPage>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
